import { useState, useRef } from "react";
import { BookMarked, Search, Loader2, Sparkles, BookOpen, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function EntryVerses({ number }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRefs = async (offset = 0) => {
    setLoading(true);
    try {
      const res = await api.get(`/lexicon/references?number=${number}&limit=10&offset=${offset}`);
      setData((prev) => ({
        count: res.data.count,
        references: offset === 0 ? res.data.references : [...(prev?.references || []), ...res.data.references],
      }));
    } catch (_) {
      toast.error("Could not load verses.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data) fetchRefs(0);
  };

  return (
    <div className="mt-3">
      <button
        data-testid={`lexicon-verses-toggle-${number}`}
        onClick={toggle}
        className="flex items-center gap-1.5 text-sm text-[#9B7E4E] hover:text-[#2C2A29] transition-colors duration-300"
      >
        <BookOpen className="w-4 h-4" strokeWidth={1.75} />
        {open ? "Hide verses" : "Show verses"}
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3" data-testid={`lexicon-verses-${number}`}>
          {loading && !data ? (
            <div className="py-4"><Loader2 className="w-5 h-5 text-[#2C2A29] animate-spin" /></div>
          ) : data && data.count === 0 ? (
            <p className="font-body text-sm text-[#7A756E]">No KJV occurrences indexed for this word.</p>
          ) : data ? (
            <>
              <p className="font-body text-xs text-[#7A756E] mb-3">Appears in {data.count} KJV verse{data.count === 1 ? "" : "s"}</p>
              <ul className="space-y-3">
                {data.references.map((r, i) => (
                  <li key={i} className="pl-3 border-l-2 border-[#EAE6DF]">
                    <Link
                      to="/bible"
                      state={{ goto: { book: r.book, chapter: r.chapter, verse: r.verse } }}
                      data-testid={`lexicon-verse-link-${number}-${i}`}
                      className="font-body text-sm text-[#9B7E4E] hover:text-[#2C2A29] transition-colors duration-300"
                    >
                      {r.reference}
                    </Link>
                    <p className="font-decree text-base text-[#2C2A29] leading-relaxed mt-0.5">{r.text}</p>
                  </li>
                ))}
              </ul>
              {data.references.length < data.count && (
                <button
                  onClick={() => fetchRefs(data.references.length)}
                  disabled={loading}
                  data-testid={`lexicon-verses-more-${number}`}
                  className="mt-4 flex items-center gap-2 text-sm text-[#4A4744] hover:text-[#2C2A29] transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Show more verses
                </button>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function Lexicon() {  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [study, setStudy] = useState(null);
  const [studying, setStudying] = useState(false);
  const studyRef = useRef(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    setStudy(null);
    try {
      const { data } = await api.get(`/lexicon/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data.results);
    } catch (err) {
      toast.error("Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const generateStudy = async () => {
    if (!q.trim()) return;
    setStudying(true);
    try {
      const { data } = await api.post("/lexicon/study", { topic: q.trim() });
      setStudy(data);
      setTimeout(() => studyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate study.");
    } finally {
      setStudying(false);
    }
  };

  const inputCls =
    "flex-1 bg-transparent py-3.5 text-lg text-[#2C2A29] placeholder-[#A39C90] focus:outline-none";

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="lexicon-page">
      <div className="flex items-center gap-3 mb-6">
        <BookMarked className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Lexicon · Strong's Concordance</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#2C2A29] tracking-tight">Word studies</h1>
      <p className="font-decree text-xl text-[#4A4744] mt-4 leading-relaxed">
        Search a word or topic to uncover the original Hebrew and Greek behind it, drawn from Strong's Concordance.
      </p>

      <form onSubmit={search} className="mt-8">
        <div className="flex items-center gap-2 border border-[#D9D4CC] rounded-sm px-4 bg-[#FDFBF7]">
          <Search className="w-5 h-5 text-[#A39C90]" />
          <input
            data-testid="lexicon-input"
            autoFocus
            className={inputCls}
            placeholder="e.g. love, faith, peace, or a Strong's number like G26"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button
            data-testid="lexicon-search-button"
            type="submit"
            disabled={!q.trim() || searching}
            className="flex items-center gap-2 px-7 py-3 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-40"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
          <button
            data-testid="lexicon-study-button"
            type="button"
            onClick={generateStudy}
            disabled={!q.trim() || studying}
            className="flex items-center gap-2 px-7 py-3 border border-[#D9D4CC] text-[#2C2A29] rounded-sm hover:bg-[#EAE6DF] transition-colors duration-300 disabled:opacity-40"
          >
            {studying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate word study
          </button>
        </div>
      </form>

      {study && (
        <div data-testid="lexicon-study" ref={studyRef} className="mt-10 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-7 animate-fade-up">
          <p className="font-body text-xs uppercase tracking-[0.15em] text-[#7A756E] mb-3">Word study · {study.topic}</p>
          {study.study.split(/\n\n+/).map((para, i) => (
            <p key={i} className="font-decree text-lg leading-relaxed text-[#2C2A29] mb-4 last:mb-0">{para}</p>
          ))}
        </div>
      )}

      {results !== null && (
        <div className="mt-10" data-testid="lexicon-results">
          {results.length === 0 ? (
            <p className="font-decree text-xl text-[#4A4744]">No entries found. Try another word.</p>
          ) : (
            <>
              <p className="font-body text-sm text-[#7A756E] mb-5">{results.length} {results.length === 1 ? "entry" : "entries"} found</p>
              <ul className="space-y-5">
                {results.map((e) => (
                  <li key={e.number} data-testid={`lexicon-entry-${e.number}`} className="border-b border-[#EAE6DF] pb-5 last:border-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="font-heading text-2xl text-[#2C2A29]">{e.lemma}</span>
                      <span className="font-body text-sm text-[#7A756E] italic">{e.translit}{e.pron ? ` · ${e.pron}` : ""}</span>
                      <span className="ml-auto text-xs uppercase tracking-wide px-2.5 py-1 border border-[#D9D4CC] rounded-full text-[#7A756E]">
                        {e.lang} · {e.number}
                      </span>
                    </div>
                    {e.strongs_def && <p className="font-body text-base text-[#2C2A29] mt-2 leading-relaxed">{e.strongs_def}</p>}
                    {e.kjv_def && <p className="font-body text-sm text-[#7A756E] mt-2"><span className="uppercase tracking-wide text-xs">KJV usage:</span> {e.kjv_def}</p>}
                    <EntryVerses number={e.number} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <p className="mt-12 pt-6 border-t border-[#EAE6DF] font-body text-xs text-[#A39C90]">
        Lexicon data from Strong's Concordance (Open Scriptures), licensed under CC BY-SA.
      </p>
    </div>
  );
}
